<template>
	<div v-if="showTable" :class="{ 'the-table-pull-up': filterTemplates !== undefined && filterTemplates.length > 0 }">
		<v-dialog v-model="filterPopup.show" max-width="600" persistent>
			<v-card>
				<v-card-text>
					<div class="filter-popup-title">
						{{ $t('labels.additionalFilters') }}
					</div>
					<div class="filter-popup-left">
						<div
							v-for="(filter, key) in filterFields"
							:key="key"
							:class="{
								'filter-popup-field': true,
								'filter-popup-field-selected': filterPopup.field == filter.value,
							}"
							@click="popupFieldChanged(filter.value)"
						>
							{{ filter.label }}
							<span
								v-if="filterPopup.filters[filter.value] !== undefined"
								class="filter-popup-field-marker"
								><v-icon color="success" small>fa-circle</v-icon></span
							>
						</div>
					</div>
					<div class="filter-popup-right">
						<FilterField
							v-if="filterPopup.showFilterField"
							v-model="filterPopup.value"
							:name="filterPopup.field"
							:text="filterPopup.label"
							:type="filterPopup.type"
						></FilterField>
					</div>
					<div class="filter-popup-clear"></div>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary" @click="saveFilters()">{{ $t('buttons.ok') }}</v-btn>
					<v-btn color="basicButton" @click="resetFilterPopup()">{{ $t('buttons.cancel') }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="headersPopup.show" max-width="600" persistent>
			<v-card>
				<v-card-text class="pt-5">
					<DropdownMultiple
						:label="$t('fields.selectHeaders')"
						v-model="headersPopup.selectedHeaders"
						:data="headersForTags"
					></DropdownMultiple>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary" @click="saveHeaders()">{{ $t('buttons.ok') }}</v-btn>
					<v-btn color="basicButton" @click="resetHeadersPopup()">{{ $t('buttons.cancel') }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<template v-if="filterTemplates !== undefined">
			<div v-if="filterTemplates.length > 0" class="the-table-filter-templates-buttons">
				<v-btn
					v-for="(template, key) in filterTemplates"
					:key="key"
					:class="{ warning: selectedFilterTemplate == key }"
					@click="selectFilterTemplate(key)"
				>
					{{ template.name }}
				</v-btn>
			</div>
		</template>
		<div class="the-table-actions-and-filters">
			<div
				:class="{
					'the-table-filters': !$vuetify.theme.dark,
					'the-table-filters-dark-theme': $vuetify.theme.dark,
				}"
			>
				<span
					v-for="(filter, key) in additionalFiltersData"
					:key="key"
					:class="{
						'filter-pill': !$vuetify.theme.dark,
						'filter-pill-dark-theme': $vuetify.theme.dark,
					}"
				>
					<span class="filter-pill-button" @click="removeFilter(key)">
						<v-icon small>fa-trash</v-icon>
					</span>
					<b>{{ getFilterPreviewData(key).field }}:</b>
					{{ getFilterPreviewData(key).value }}
				</span>
			</div>
			<div class="the-table-actions">
				<v-tooltip v-if="zip" bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn slot="activator" icon @click="zipClick()" v-bind="attrs" v-on="on">
							<v-icon small>fa-file-archive</v-icon>
						</v-btn>
					</template>
					<span>{{ $t('tooltips.zip') }}</span>
				</v-tooltip>
				<v-tooltip v-if="excel" bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn slot="activator" icon @click="excelClick()" v-bind="attrs" v-on="on">
							<v-icon small>fa-file-excel</v-icon>
						</v-btn>
					</template>
					<span>{{ $t('tooltips.excel') }}</span>
				</v-tooltip>
				<v-tooltip v-if="additionalFilters.length > 0" bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn slot="activator" icon @click="filterClick()" v-bind="attrs" v-on="on">
							<v-icon small>fa-filter</v-icon>
						</v-btn>
					</template>
					<span>{{ $t('tooltips.additionalFilters') }}</span>
				</v-tooltip>
				<v-tooltip v-if="additionalFilters.length > 0" bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn slot="activator" icon @click="resetFiltersClick()" v-bind="attrs" v-on="on">
							<v-icon small>fa-recycle</v-icon>
						</v-btn>
					</template>
					<span>{{ $t('tooltips.resetFilters') }}</span>
				</v-tooltip>
				<v-tooltip v-if="headerSelectionKey != undefined && headerSelectionKey != ''" bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn slot="activator" icon @click="headerSelectionClick()" v-bind="attrs" v-on="on">
							<v-icon small>fa-table</v-icon>
						</v-btn>
					</template>
					<span>{{ $t('tooltips.selectHeaders') }}</span>
				</v-tooltip>
			</div>
		</div>
		<table
			:style="{ width: tableWidthComputed, height: tableHeightComputed }"
			:class="{ 'the-table': !$vuetify.theme.dark, 'the-table-dark-theme': $vuetify.theme.dark }"
		>
			<tbody>
				<tr
					:class="{
						'the-table-header': !$vuetify.theme.dark,
						'the-table-header-dark-theme': $vuetify.theme.dark,
					}"
				>
					<td
						v-for="field in filteredHeaders"
						:key="field.value"
						:class="{
							'the-table-sortable-header': checkSortable(field),
							'the-table-align-right-header': checkAlignRight(field),
						}"
						:style="{ width: fieldWidthComputed(field) }"
					>
						<span
							:class="{
								'the-table-header-title': !$vuetify.theme.dark,
								'the-table-header-title-larger-font': $myStore.state.largeFont,
							}"
							@click="toggleSort(field)"
						>
							<span v-show="sort.field === field.value && !sort.descending" class="the-table-sort-arrow">
								<v-icon small>fa-sort-up</v-icon>
							</span>
							<span v-show="sort.field === field.value && sort.descending" class="the-table-sort-arrow">
								<v-icon small>fa-sort-down</v-icon>
							</span>
							{{ field.text }}
						</span>
						<template v-if="field.type != ''">
							<FilterField
								v-model="filtersData[field.value]"
								:filter-type="'inner'"
								:name="field.value"
								:text="field.text"
								:type="field.type"
								@changes="inlineFilterChanged()"
							></FilterField>
						</template>
						<div
							:class="{
								'the-table-header-border': !$vuetify.theme.dark,
								'the-table-header-border-dark-theme': $vuetify.theme.dark,
							}"
						></div>
					</td>
				</tr>
				<tr v-if="totalRecords === 0">
					<td class="the-table-no-data" :colspan="fields.length">{{ $t('labels.noResults') }}</td>
				</tr>
				<slot></slot>
			</tbody>
		</table>
		<div
			v-if="showPagination"
			:style="{ width: tableWidthComputed }"
			:class="{
				'the-table-footer': !$vuetify.theme.dark,
				'the-table-footer-dark-theme': $vuetify.theme.dark,
			}"
		>
			<span class="the-table-footer-label">{{ $t('labels.totalRecords') }} {{ totalRecords }}</span
			>&nbsp;&nbsp;&nbsp;
			<span class="the-table-footer-label">
				{{ $t('labels.rowsPerPage') }}
			</span>
			<input v-model="rowsPerPage" />
			<span class="the-table-footer-label">
				{{ $t('labels.page') }}
			</span>
			<span v-show="currentPage > 1" class="the-table-pagination-arrow" @click="modifyCurrentPage(-1)">
				<v-icon>fa-caret-left</v-icon>
			</span>
			<input v-model="currentPage" />
			<span v-show="currentPage < totalPages" class="the-table-pagination-arrow" @click="modifyCurrentPage(1)">
				<v-icon>fa-caret-right</v-icon>
			</span>
			<span class="the-table-footer-label">
				{{ $t('labels.of') }}
				{{ totalPages }}
			</span>
		</div>
	</div>
</template>

<script>
import FilterField from './FilterField';
import DropdownMultiple from './DropdownMultiple';

export default {
	name: 'TheTable',
	components: {
		FilterField,
		DropdownMultiple,
	},
	props: {
		fields: Array,
		totalRecords: Number,
		defaultSort: Object,
		filters: Array,
		defaultRowsPerPage: Number,
		width: String,
		height: String,
		filterSaveKey: String,
		headerSelectionKey: String,
		excel: Boolean,
		showPagination: {
			type: Boolean,
			default: true,
		},
		zip: Boolean,
		additionalFilters: {
			default: () => {
				return [];
			},
			type: Array,
		},
		filterTemplates: {
			default: () => {
				return [];
			},
			type: Array,
		},
	},
	emits: ['reload', 'excel', 'zip', 'headers'],
	data() {
		return {
			showTable: false,
			currentPage: 1,
			rowsPerPage: 50,
			sort: {
				field: '',
				descending: false,
			},
			filtersData: {},
			additionalFiltersData: {},
			filtersDisplayData: {},
			tableWidthComputed: 'auto',
			tableHeightComputed: 'auto',
			filterPopup: {
				show: false,
				field: null,
				label: '',
				type: '',
				value: {
					raw: null,
					display: '',
				},
				showFilterField: false,
				filters: {},
				display: {},
			},
			headersPopup: {
				show: false,
				selectedHeaders: [],
			},
			selectedFilterTemplate: null,
			savedFiltersLoaded: false,
			selectedHeaders: [],
			firstPageOnNextReload: false,
			firstPageOnNextReloadInlineFilters: false,
			test: true,
		};
	},
	computed: {
		headersForTags() {
			let headers = [];
			for (let field of this.fields) {
				if (!field.allowFilter) {
					continue;
				}
				headers.push({
					label: field.text,
					value: field.value,
				});
			}
			return headers;
		},
		filteredHeaders() {
			let headers = [];
			for (let field of this.fields) {
				if (this.selectedHeaders.includes(field.value)) {
					headers.push(field);
				}
			}
			return headers;
		},
		totalPages() {
			if (this.rowsPerPage === 0) return 0;
			return Math.ceil(this.totalRecords / this.rowsPerPage);
		},
		filterFields() {
			let filters = [];
			for (let filter of this.additionalFilters) {
				filters.push({
					label: filter.text,
					value: filter.value,
				});
			}
			filters.sort(function (a, b) {
				if (a.label < b.label) {
					return -1;
				}
				if (a.label > b.label) {
					return 1;
				}
				return 0;
			});
			return filters;
		},
		filtersToShow() {
			let output = [];
			for (let key in this.additionalFiltersData) {
				output.push({
					field: key,
					value: this.additionalFiltersData[key],
				});
			}
			return output;
		},
	},
	watch: {
		selectedHeaders() {
			this.$emit('headers', this.selectedHeaders);
			if (this.headerSelectionKey != undefined && this.headerSelectionKey != '') {
				this.$APICall(
					'post',
					'user-data/' + this.headerSelectionKey,
					{
						data: this.selectedHeaders,
					},
					(data, success) => {},
					false
				);
			}
		},

		currentPage() {
			if (!this.firstPageOnNextReload) this.reload();
		},
		rowsPerPage() {
			this.reload();
		},
		height() {
			if (this.height !== undefined) {
				this.tableHeightComputed = this.height;
			}
		},
	},
	methods: {
		headerSelectionClick() {
			this.headersPopup.selectedHeaders = JSON.parse(JSON.stringify(this.selectedHeaders));
			this.headersPopup.show = true;
		},
		saveHeaders() {
			this.selectedHeaders = JSON.parse(JSON.stringify(this.headersPopup.selectedHeaders));
			this.resetHeadersPopup();
		},
		resetHeadersPopup() {
			this.headersPopup = {
				show: false,
				selectedHeaders: [],
			};
		},
		fieldWidthComputed(field) {
			if (field.width !== undefined) {
				return field.width;
			}
			return 'auto';
		},
		checkSortable(field) {
			return field.sortable !== false;
		},
		checkAlignRight(field) {
			return field.alignRight;
		},
		modifyCurrentPage(modifier) {
			this.currentPage = parseInt(this.currentPage) + modifier;
		},
		toggleSort(field) {
			if (!this.checkSortable(field)) return;
			if (this.sort.field !== field.value) {
				this.sort.field = field.value;
				this.sort.descending = false;
			} else {
				this.sort.descending = !this.sort.descending;
			}
			this.reload();
		},
		isSearchInUse(obj) {
			for (const key in obj) {
				if (obj[key] !== '') {
					return true;
				}
			}
			return false;
		},
		reload() {
			if (!this.savedFiltersLoaded) return;
			if (this.filterSaveKey != undefined && this.filterSaveKey != '') {
				this.$APICall(
					'post',
					'user-data/' + this.filterSaveKey,
					{
						data: {
							filters_data: this.filtersData,
							additional_filters_data: this.additionalFiltersData,
							filters_display_data: this.filtersDisplayData,
						},
					},
					(data, success) => {},
					false
				);
			}
			let searchRawForTemplate = {};
			let search = {};
			for (let key in this.additionalFiltersData) {
				searchRawForTemplate[key] = this.additionalFiltersData[key];
				if (
					typeof this.additionalFiltersData[key] !== 'object' ||
					Array.isArray(this.additionalFiltersData[key])
				) {
					search[key] = this.additionalFiltersData[key];
				} else {
					for (let objectKey in this.additionalFiltersData[key]) {
						search[key + '_' + objectKey] = this.additionalFiltersData[key][objectKey];
					}
				}
			}
			for (let key in this.filtersData) {
				searchRawForTemplate[key] = this.filtersData[key].raw;
				if (typeof this.filtersData[key].raw !== 'object') {
					search[key] = this.filtersData[key].raw;
				} else {
					for (let objectKey in this.filtersData[key].raw) {
						search[key + '_' + objectKey] = this.filtersData[key].raw[objectKey];
					}
				}
			}

			this.selectedFilterTemplate = null;
			if (this.filterTemplates !== undefined) {
				for (let templateKey in this.filterTemplates) {
					let template = this.filterTemplates[templateKey];
					let term = true;
					for (let field in template.filters) {
						if (
							JSON.stringify(template.filters[field].raw) !== JSON.stringify(searchRawForTemplate[field])
						) {
							term = false;
						}
					}
					for (let field in searchRawForTemplate) {
						let type = null;
						for (let rawField of this.fields) {
							if (rawField.value === field) {
								type = rawField.type;
								break;
							}
						}
						if (type === null) {
							for (let rawField of this.additionalFilters) {
								if (rawField.value === field) {
									type = rawField.type;
									break;
								}
							}
						}
						let defaultValue = this.getFilterDefault({ type: type });
						if (
							JSON.stringify(searchRawForTemplate[field]) !== JSON.stringify(defaultValue) &&
							template.filters[field] === undefined
						) {
							term = false;
						}
					}
					if (term) {
						this.selectedFilterTemplate = templateKey;
						break;
					}
				}
			}

			// if (this.firstPageOnNextReloadOfInlineFilters) {
			// 	this.currentPage = 1;
			// }

			if (this.firstPageOnNextReload) {
				this.currentPage = 1;
			}
			this.$nextTick(() => {
				this.firstPageOnNextReload = false;
			});

			let payload = {
				pagination: {
					sortDesc: this.sort.descending,
					page: this.currentPage,
					itemsPerPage: this.rowsPerPage,
					sortBy: [this.sort.field],
					totalItems: 0,
				},
				search: search,
			};

			this.$emit('reload', payload);
		},
		excelClick() {
			this.$emit('excel', this.request);
		},
		zipClick() {
			this.$emit('zip', this.request);
		},
		filterClick() {
			this.filterPopup.filters = JSON.parse(JSON.stringify(this.additionalFiltersData));
			this.filterPopup.display = JSON.parse(JSON.stringify(this.filtersDisplayData));
			this.filterPopup.show = true;
			if (this.filterFields.length > 0) {
				let vm = this;
				this.$nextTick(() => {
					vm.popupFieldChanged(vm.filterFields[0].value);
				});
			}
		},
		resetFilterPopup() {
			this.filterPopup = {
				show: false,
				field: null,
				label: '',
				type: '',
				value: {
					raw: null,
					display: '',
				},
				showFilterField: false,
				filters: {},
				display: {},
			};
		},
		popupFieldChanged(field) {
			if (this.filterPopup.field != null) {
				this.applyFilter();
			}
			this.filterPopup.field = field;
			this.filterPopup.showFilterField = false;
			if (this.filterPopup.field != null) {
				for (let filter of this.additionalFilters) {
					if (filter.value == this.filterPopup.field) {
						this.filterPopup.type = filter.type;
						this.filterPopup.label = filter.text;

						if (this.filterPopup.filters[this.filterPopup.field] !== undefined) {
							this.filterPopup.value.raw = JSON.parse(
								JSON.stringify(this.filterPopup.filters[this.filterPopup.field])
							);
							this.filterPopup.value.display = JSON.parse(
								JSON.stringify(this.filterPopup.display[this.filterPopup.field])
							);
						} else {
							this.filterPopup.value.raw = this.getFilterDefault(filter);
						}

						this.$nextTick(() => {
							this.filterPopup.showFilterField = true;
						});
					}
				}
			}
		},
		applyFilter() {
			this.firstPageOnNextReload = true;

			this.filterPopup.filters[this.filterPopup.field] = this.filterPopup.value.raw;
			this.filterPopup.display[this.filterPopup.field] = this.filterPopup.value.display;

			let defaultValue = this.getFilterDefault({ type: this.filterPopup.type });
			if (JSON.stringify(this.filterPopup.value.raw) === JSON.stringify(defaultValue)) {
				delete this.filterPopup.filters[this.filterPopup.field];
				delete this.filterPopup.display[this.filterPopup.field];
			}
		},
		saveFilters() {
			if (this.filterPopup.field != null) {
				this.applyFilter();
			}
			this.additionalFiltersData = JSON.parse(JSON.stringify(this.filterPopup.filters));
			this.filtersDisplayData = JSON.parse(JSON.stringify(this.filterPopup.display));
			this.resetFilterPopup();
			this.reload();
		},
		removeFilter(field) {
			if (this.additionalFiltersData[field] !== undefined) {
				let newData = {};
				for (let key in this.additionalFiltersData) {
					if (key != field) {
						newData[key] = this.additionalFiltersData[key];
					}
				}
				this.additionalFiltersData = JSON.parse(JSON.stringify(newData));
				this.reload();
			}
		},
		getFilterPreviewData(field) {
			let data = {
				field: field,
				value: this.filtersDisplayData[field],
			};
			for (let filter of this.additionalFilters) {
				if (filter.value === field) {
					data.field = filter.text;
				}
			}
			return data;
		},
		getFilterDefault(filter) {
			if (filter.default) return filter.default;
			let defaultValue = '';
			if (filter.type.includes('price')) {
				defaultValue = null;
			}
			if (filter.type.includes('dropdown')) {
				defaultValue = null;
			}
			if (filter.type == 'boolean') {
				defaultValue = null;
			}
			if (filter.type == 'boolean_partial') {
				defaultValue = null;
			}
			if (filter.type.includes('tags')) {
				defaultValue = [];
			}
			if (filter.type.includes('multiple')) {
				defaultValue = [];
			}
			if (filter.type.includes('select_all')) {
				defaultValue = [];
			}
			if (filter.type.includes('date')) {
				defaultValue = null;
			}
			if (filter.type == 'string') {
				defaultValue = '';
			}
			if (filter.type == 'date_range') {
				defaultValue = {
					from: null,
					to: null,
				};
			}
			if (filter.type == 'price_range') {
				defaultValue = {
					from: null,
					to: null,
				};
			}
			if (filter.type == 'number_range') {
				defaultValue = {
					from: null,
					to: null,
				};
			}
			return defaultValue;
		},
		inlineFilterChanged() {
			this.$nextTick(() => {
				this.reload();
			});
		},
		selectFilterTemplate(key) {
			if (this.filterTemplates === undefined) return;
			this.resetFilters();
			this.selectedFilterTemplate = key;
			let template = this.filterTemplates[key];
			for (let filterKey in template.filters) {
				for (let filterData in this.fields) {
					if (filterData.type != '' && filterData.value === filterKey) {
						this.filtersData[filterKey].raw = template.filters[filterKey].raw;
						this.filtersData[filterKey].display = template.filters[filterKey].display;
					}
				}
				for (let filterData of this.additionalFilters) {
					if (filterData.value === filterKey) {
						this.additionalFiltersData[filterKey] = template.filters[filterKey].raw;
						this.filtersDisplayData[filterKey] = template.filters[filterKey].display;
					}
				}
			}
			this.$nextTick(() => {
				this.showTable = true;
				this.reload();
			});
		},
		resetFiltersClick() {
			this.resetFilters();
			this.reload();
		},
		resetFilters() {
			for (let field of this.$props.fields) {
				if (field.type != '') {
					this.filtersData[field.value] = {
						raw: this.getFilterDefault(field),
						display: '',
					};
				}
			}
			this.additionalFiltersData = {};
			this.filtersDisplayData = {};
		},
		selectDefaultHeaders() {
			let headers = [];
			for (let field of this.fields) {
				headers.push(field.value);
			}
			this.selectedHeaders = headers;
		},
		loadDefaults() {
			for (let field of this.fields) {
				if (field.type != '' && field.default !== undefined) {
					this.filtersData[field.value] = field.default;
				}
			}
			for (let field of this.additionalFilters) {
				if (field.default !== undefined) {
					this.additionalFiltersData[field.value] = field.default;
					this.filtersDisplayData[field.value] = field.defaultDisplay;
				}
			}
		},
	},
	mounted() {
		this.resetFilters();
		if (this.defaultRowsPerPage !== undefined) {
			this.rowsPerPage = this.defaultRowsPerPage;
		}
		if (this.defaultSort !== undefined) {
			this.sort = this.defaultSort;
		} else {
			if (this.fields.length > 0) {
				this.sort = {
					field: this.fields[0].value,
					descending: false,
				};
			}
		}
		if (this.width !== undefined) {
			this.tableWidthComputed = this.width;
		}
		if (this.height !== undefined) {
			this.tableHeightComputed = this.height;
		}

		if (this.filterSaveKey != undefined && this.filterSaveKey != '') {
			let vm = this;
			this.$APICall(
				'get',
				'user-data/' + this.filterSaveKey,
				{},
				(data, success) => {
					if (success && data.data != null) {
						if (data.data.data.filters_data.length !== undefined) {
							data.data.data.filters_data = {};
						}
						if (data.data.data.additional_filters_data.length !== undefined) {
							data.data.data.additional_filters_data = {};
						}
						if (data.data.data.filters_display_data.length !== undefined) {
							data.data.data.filters_display_data = {};
						}
						vm.filtersData = JSON.parse(JSON.stringify(data.data.data.filters_data));
						vm.additionalFiltersData = JSON.parse(JSON.stringify(data.data.data.additional_filters_data));
						vm.filtersDisplayData = JSON.parse(JSON.stringify(data.data.data.filters_display_data));
					}
					if (Object.keys(vm.filtersData).length + Object.keys(vm.additionalFiltersData).length === 0) {
						vm.loadDefaults();
					}
					vm.$nextTick(() => {
						vm.savedFiltersLoaded = true;
						vm.showTable = true;
						vm.reload();
					});
				},
				false
			);
		} else {
			this.savedFiltersLoaded = true;
			if (this.filterTemplates !== undefined && this.filterTemplates.length > 0) {
				for (let templateIdx in this.filterTemplates) {
					if (this.filterTemplates[templateIdx].default) {
						this.selectFilterTemplate(templateIdx);
					}
				}
			} else {
				this.loadDefaults();
			}
			this.$nextTick(() => {
				this.showTable = true;
				this.reload();
			});
		}
		if (this.headerSelectionKey != undefined && this.headerSelectionKey != '') {
			let vm = this;
			this.$APICall('get', 'user-data/' + this.headerSelectionKey, {}, (data, success) => {
				data = data.data;
				if (data === null) {
					vm.selectDefaultHeaders();
				} else {
					data = data.data;
					vm.selectedHeaders = data;
				}
			});
		} else {
			this.selectDefaultHeaders();
		}
	},
};
</script>

<style scoped>
.the-table-pull-up {
	margin-top: -48px;
}

.the-table {
	clear: both;
	margin: 5px 5px 0;
	display: block;
	overflow-x: auto;
	background: white;
	border-collapse: collapse;
	scrollbar-color: lightgray #efefef;
	scrollbar-width: thin;
	white-space: nowrap;
}

.the-table-dark-theme {
	clear: both;
	margin: 5px 5px 0;
	display: block;
	overflow-x: auto;
	background: #1e1e1e;
	border-collapse: collapse;
	scrollbar-color: lightgray #efefef;
	scrollbar-width: thin;
	white-space: nowrap;
	color: #dbdbdb;
}

.the-table::-webkit-scrollbar {
	background: #f9f9f9;
	width: 10px;
}

.the-table-dark-theme::-webkit-scrollbar {
	background: #f9f9f9;
	width: 10px;
}

.the-table::-webkit-scrollbar:hover {
	background: #ececec;
}

.the-table-dark-theme::-webkit-scrollbar:hover {
	background: #ececec;
}

.the-table::-webkit-scrollbar-thumb {
	background: lightgray;
}

.the-table-dark-theme::-webkit-scrollbar-thumb {
	background: lightgray;
}

.the-table::-webkit-scrollbar-thumb:hover {
	background: #ababab;
}

.the-table-dark-theme::-webkit-scrollbar-thumb:hover {
	background: #ababab;
}

.the-table td {
	padding: 5px;
	border-bottom: 1px solid #efefef;
}

.the-table-dark-theme td {
	padding: 5px;
	border-bottom: 1px solid #808080;
}

.the-table tr:last-child td {
	border: none;
}

.the-table-dark-theme tr:last-child td {
	border: none;
}

.the-table-header td {
	position: sticky;
	top: 0;
	z-index: 1;
	vertical-align: top;
	background: white !important;
	padding: 10px 5px;
}

.the-table-header-dark-theme td {
	position: sticky;
	top: 0;
	z-index: 1;
	vertical-align: top;
	background: #1e1e1e !important;
	padding: 10px 5px;
}

.the-table-header-border {
	position: absolute;
	bottom: 0;
	left: 0;
	height: 1px;
	background: #efefef;
	width: 100%;
}

.the-table-header-border-dark-theme {
	position: absolute;
	bottom: 0;
	left: 0;
	height: 1px;
	background: #1e1e1e;
	width: 100%;
}

.the-table-footer {
	box-sizing: border-box;
	margin-right: 5px;
	margin-left: 5px;
	padding-left: 10px;
	padding-right: 10px;
	background: white;
	text-align: right;
	line-height: 30px;
	border-top: 1px solid #efefef;
}

.the-table-footer-dark-theme {
	box-sizing: border-box;
	margin-right: 5px;
	margin-left: 5px;
	padding-left: 10px;
	padding-right: 10px;
	background: #1e1e1e;
	text-align: right;
	line-height: 30px;
	border-top: 1px solid #efefef;
}

.the-table-footer-dark-theme input {
	color: rgba(255, 255, 255, 0.7);
}

.the-table-footer-label {
	display: inline-block;
	line-height: 54px;
}

.the-table-pagination {
	text-align: right;
}

.the-table-pagination-arrow {
	cursor: pointer;
	display: inline-block;
	height: 30px;
	line-height: 30px;
	margin-left: 5px;
	margin-right: 5px;
}

.the-table-pagination-arrow i {
	line-height: 18px;
}

.the-table-sort-arrow {
	float: left;
	margin-right: 5px;
}

.the-table-align-right-header {
	text-align: right;
}

.the-table-sortable-header {
	cursor: pointer;
}

.the-table tr:hover {
	background: #f9f9f9;
}

.the-table-dark-theme tr:hover {
	background: #4a4a4a;
}

.the-table-footer td {
	background: violet !important;
}

.the-table-footer-dark-theme td {
	background: violet !important;
}

.the-table tbody {
	display: table;
	width: 100%;
}

.the-table-dark-theme tbody {
	display: table;
	width: 100%;
}

.the-table thead {
	display: table;
	width: 100%;
}

.the-table-dark-theme thead {
	display: table;
	width: 100%;
}

.the-table-button {
	cursor: pointer;
	margin: 5px;
}

.the-table-linked-cell {
	cursor: pointer;
}

.the-table-filters td {
	padding: 0;
	background: #efefef;
}

.the-table-filters-dark-theme td {
	padding: 0;
	background: #3d3d3d;
}

.the-table-actions {
	width: 220px;
	float: right;
	text-align: right;
}

.the-table-filters {
	width: calc(100% - 200px);
	float: left;
	text-align: left;
}

.the-table-footer input {
	width: 30px;
	border: 1px solid grey;
	text-align: center;
	margin-left: 5px;
	margin-right: 5px;
}

.the-table-footer-dark-theme input {
	width: 30px;
	border: 1px solid grey;
	text-align: center;
	margin-left: 5px;
	margin-right: 5px;
}

.filter-pill {
	display: inline-block;
	background: #ffe5b4;
	padding: 3px 5px;
	margin-right: 5px;
}

.filter-pill-dark-theme {
	display: inline-block;
	background: #3d3d3d;
	padding: 3px 5px;
	margin-right: 5px;
}

.filter-pill-button {
	display: inline-block;
	margin-right: 3px;
	cursor: pointer;
}

.the-table-header-title {
	display: inline-block;
	color: rgba(0, 0, 0, 0.54);
	font-weight: 500;
	font-size: 12px;
	cursor: pointer;
	width: 100%;
}

.the-table-header-title-larger-font {
	font-size: 16px;
}

.the-table-filter-templates-buttons {
	margin-left: -8px;
	text-align: right;
}

.filter-popup-clear {
	clear: both;
}

.filter-popup-left {
	float: left;
	width: 200px;
	margin-left: -16px;
}

.filter-popup-right {
	float: right;
	width: calc(100% - 200px);
	padding-top: 30px;
}

.filter-popup-field {
	cursor: pointer;
	padding: 5px;
	border-bottom: 1px solid gray;
	border-right: 1px solid gray;
}

.filter-popup-field-selected {
	font-weight: bold;
	border-right: none;
}

.filter-popup-field-marker {
	float: right;
}

.filter-popup-title {
	width: calc(100% + 48px);
	clear: both;
	margin-left: -24px;
	padding: 10px;
	color: white;
	background: #1e2933;
}

.the-table-no-data {
	text-align: center;
	padding: 15px !important;
}
</style>
